/**
 * TenantStatusBanner — Phase 10H tenant lifecycle banner.
 *
 * Reads the lifecycle payload from /access/matrix (already cached in
 * AccessMatrixContext) and renders a context-aware banner at the top
 * of the AppShell. Hidden for platform users (they have the platform
 * banner instead).
 *
 * Statuses handled (banner shapes are sourced from the backend SSOT
 * `services.tenant_status_service.status_banner`):
 *   - trial (when ≤ 5 days remain)   → info
 *   - grace_period                   → warning
 *   - suspended                      → critical
 *   - cancelled                      → neutral (read-only mode)
 *
 * Active tenants outside these conditions render NULL (no banner).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, Info, Archive, CheckCircle2 } from "lucide-react";
import { useAccessMatrix } from "@/context/AccessMatrixContext";
import { useAuth } from "@/context/AuthContext";
import UpgradeModal from "@/components/billing/UpgradeModal";


// Owner-level roles authorized to launch the Stripe upgrade flow.
// Other roles can still see the banner but the CTA links to /billing.
const OWNER_ROLES = new Set(["restaurant_owner", "hotel_owner", "tenant_owner"]);


const TONE_STYLES = {
  success: {
    bg: "bg-gradient-to-r from-[#ECFDF5] to-[#D1FAE5]",
    border: "border-[#A7F3D0]",
    title: "text-[#064E3B]",
    text: "text-[#065F46]",
    cta: "bg-[#059669] hover:bg-[#047857] text-white",
    Icon: CheckCircle2,
    iconColor: "text-[#059669]",
  },
  info: {
    bg: "bg-gradient-to-r from-[#EFF6FF] to-[#DBEAFE]",
    border: "border-[#BFDBFE]",
    title: "text-[#1E3A8A]",
    text: "text-[#1E40AF]",
    cta: "bg-[#1D4ED8] hover:bg-[#1E40AF] text-white",
    Icon: Info,
    iconColor: "text-[#1D4ED8]",
  },
  warning: {
    bg: "bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7]",
    border: "border-[#FCD34D]",
    title: "text-[#78350F]",
    text: "text-[#92400E]",
    cta: "bg-[#B45309] hover:bg-[#92400E] text-white",
    Icon: AlertTriangle,
    iconColor: "text-[#B45309]",
  },
  critical: {
    bg: "bg-gradient-to-r from-[#FEF2F2] to-[#FEE2E2]",
    border: "border-[#FCA5A5]",
    title: "text-[#7F1D1D]",
    text: "text-[#991B1B]",
    cta: "bg-[#B91C1C] hover:bg-[#991B1B] text-white",
    Icon: AlertCircle,
    iconColor: "text-[#B91C1C]",
  },
  neutral: {
    bg: "bg-gradient-to-r from-[#F1F5F9] to-[#E2E8F0]",
    border: "border-[#CBD5E1]",
    title: "text-[#0F172A]",
    text: "text-[#475569]",
    cta: "bg-[#102A43] hover:bg-[#0B1B2C] text-white",
    Icon: Archive,
    iconColor: "text-[#475569]",
  },
};


export default function TenantStatusBanner() {
  const { tenant_status, isPlatform, loading } = useAccessMatrix();
  const { user } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Platform users see the PlatformModeBanner; lifecycle banner is tenant-only.
  if (loading || isPlatform) return null;
  const banner = tenant_status?.banner;
  if (!banner) return null;

  const style = TONE_STYLES[banner.tone] || TONE_STYLES.info;
  const Icon = style.Icon;
  const isOwner = user && OWNER_ROLES.has(user.role);
  const showUpgradeCTA = isOwner && banner.show_upgrade_modal;

  const renderCta = () => {
    if (!banner.cta_label || !banner.cta_href) return null;
    // Owner-level + trial/trial_expired → open upgrade modal in-app
    if (showUpgradeCTA) {
      return (
        <button
          type="button"
          onClick={() => setUpgradeOpen(true)}
          className={`inline-flex items-center justify-center px-4 py-1.5 rounded-md text-xs font-semibold shrink-0 ${style.cta} transition-colors`}
          data-testid="tenant-status-cta"
        >
          {banner.cta_label}
        </button>
      );
    }
    const isExternal = banner.cta_href.startsWith("mailto:") || banner.cta_href.startsWith("http");
    if (isExternal) {
      return (
        <a
          href={banner.cta_href}
          className={`inline-flex items-center justify-center px-4 py-1.5 rounded-md text-xs font-semibold shrink-0 ${style.cta} transition-colors`}
          data-testid="tenant-status-cta"
        >
          {banner.cta_label}
        </a>
      );
    }
    return (
      <Link
        to={banner.cta_href}
        className={`inline-flex items-center justify-center px-4 py-1.5 rounded-md text-xs font-semibold shrink-0 ${style.cta} transition-colors`}
        data-testid="tenant-status-cta"
      >
        {banner.cta_label}
      </Link>
    );
  };

  return (
    <>
      <div
        className={`${style.bg} ${style.border} border-b px-4 py-2.5 sticky top-0 z-30`}
        role="status"
        data-testid="tenant-status-banner"
        data-status={banner.status}
        data-tone={banner.tone}
        data-blocking={banner.blocking ? "true" : "false"}
      >
        <div className="max-w-[1400px] mx-auto flex items-center gap-3 flex-wrap sm:flex-nowrap">
          <Icon className={`w-4 h-4 shrink-0 ${style.iconColor}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold leading-tight ${style.title}`}>
              {banner.title}
              {banner.days_remaining != null && banner.status === "trial" && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider">
                  · {banner.days_remaining} {banner.days_remaining === 1 ? "giorno" : "giorni"}
                </span>
              )}
            </p>
            <p className={`text-[11px] leading-snug ${style.text} mt-0.5`}>
              {banner.message}
            </p>
          </div>
          {renderCta()}
        </div>
      </div>
      {upgradeOpen && (
        <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      )}
    </>
  );
}
